import type { MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";

export type VisualisationChoice<K extends string = "quantity" | "grossSales", U extends string = "T" | "€"> = {
    key: K;
    unit: U;
    label: string;
};

export type IngredientSupplierVisualisationChoice = VisualisationChoice<"quantity" | "grossSales", "T" | "€">;
export type ProductCompanyVisualisationChoice = VisualisationChoice<"quantity", "T">;

export type ScoreScatterPoint = {
    id: string;
    label: string;
    x: number;
    y: number;
};

export const getScoreScatterDataset = <T extends VisualisationChoice>(
    scores: MainScore | undefined,
    scoresDimension: string,
    visualisationChoice: T,
    getActivityDataValueByCompanyId: (companyId: number, visualisationChoiceKey: T["key"]) => number,
): ScoreScatterPoint[] => {
    if (scores === undefined) return [];

    const dimension = OlapScoresService.getDimension(scoresDimension, scores.dimensions);
    const dimensionValues = dimension?.values ?? [];

    return dimensionValues.flatMap((value) => {
        const scoreValue = OlapScoresService.normalizeNumericalScore(value.score.value);

        if (scoreValue === null) return [];

        const companyId = Number(value.id);
        const activityValue = getActivityDataValueByCompanyId(companyId, visualisationChoice.key);

        return [
            {
                id: value.id,
                label: value.label,
                x: activityValue,
                y: scoreValue,
            },
        ];
    });
};

export const getScoreScatterAxisDomain = (dataset: readonly ScoreScatterPoint[]) => {
    if (dataset.length === 0) return undefined;

    const xs = dataset.map(({ x }) => x);
    const ys = dataset.map(({ y }) => y);
    const xMinData = Math.min(...xs);
    const xMaxData = Math.max(...xs);
    const yMinData = Math.min(...ys);
    const yMaxData = Math.max(...ys);
    const padRatio = 0.1;
    const xMin = xMinData - padRatio * xMinData;
    const xMax = xMaxData + padRatio * xMaxData;
    const yMin = yMinData - padRatio * Math.abs(yMinData);
    const yMax = yMaxData + padRatio * Math.abs(yMaxData);

    return {
        xMin: xMinData === xMaxData && xMinData === 0 ? 0 : Math.max(0, xMin),
        xMax: xMinData === xMaxData && xMaxData === 0 ? 1 : xMax,
        yMin: yMinData === yMaxData && yMinData === 0 ? 0 : yMin,
        yMax: yMinData === yMaxData && yMaxData === 0 ? 1 : yMax,
    };
};
